/**
 * Playbooks send PRD, Unit U9 (R10 / Key Technical Decision 10) — per-field
 * caret tracking, and the insert that uses it.
 *
 * ── WHAT THIS EXISTS TO FIX ───────────────────────────────────────────────────
 * Launcher's own variable picker appends (`insertVariable` in
 * `mini-apps/launcher/components/CopyEditor.tsx`: `setDraftSubject(s => s + v)`),
 * so a rep who wants `Hi {{First Name}},` has to insert the token and then
 * retype the greeting around it. Inserting at the cursor means knowing where
 * the cursor WAS when the rep left the field to click the Tokens button — by
 * the time the click lands, the field has already lost focus and its own
 * `selectionStart` is no longer meaningful. So the caret is recorded as the rep
 * moves it and read back at insert time.
 *
 * ── PER-FIELD PROPS, NOT GLOBAL `document` LISTENERS ──────────────────────────
 * The prototype tracked this with `document`-level `focusin`/`keyup`/`mouseup`
 * listeners plus a `state.lastFocused` singleton (`recordCopyFocus`), which was
 * vanilla-JS scaffolding for a page with one editor open at a time. Playbooks
 * renders many step editors at once (one per rung, plus the review cards), so a
 * global singleton leaks across cards: open the picker on step 3's body and the
 * insert can land in step 1's subject. Every handler here is bound to one
 * field key instead, and `insert` refuses a key it has no record for by falling
 * back to "end of that field" rather than writing into whatever was focused
 * last.
 *
 * ── REFS, NEVER STATE ─────────────────────────────────────────────────────────
 * CLAUDE.md "Draft State Under SSE": every editing surface in the v4 tree gets
 * a parent re-render on any `artifact:updated` SSE event. Caret position in
 * `useState` would mean a re-render on every keystroke and mouse move inside a
 * copy field, in the same commit path as the draft store — so all of it lives
 * in refs, which is also the correct model: nothing here is rendered, it is
 * bookkeeping read only inside event handlers.
 *
 * None of these refs carry lifetime state (no `cancelled`/`mounted`/generation
 * counter), and the hook registers no effect at all, so there is nothing for
 * the StrictMode double-invoke rule to reset — a stale caret from a previous
 * mount is impossible because a remount re-mounts the fields too, and a caret
 * that IS stale (the text shrank under it) is already handled by
 * {@link insertTokenAt}'s clamp rather than by invalidation.
 *
 * ── `lastField` IS STATE, AND THAT IS DELIBERATE (prod QA, 2026-09-16) ────────
 * {@link CaretTracker.lastField} is the one thing here that is not a ref,
 * because it is the only thing here that is RENDERED: the picker's popover
 * shows "Insert into Subject" / "Insert into Body", and a ref would leave that
 * header stale. It does not reintroduce the per-keystroke re-render the refs
 * above exist to avoid — {@link markActive}'s updater is idempotent, so React
 * bails out of every event within the SAME field and commits only on a real
 * field CHANGE. It carries no cross-mount lifetime either, so the StrictMode
 * rule in CLAUDE.md (which bites refs, not state) has nothing to reset.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import type { RefObject, SyntheticEvent } from 'react';

import { insertTokenAt } from './token-insert';

/** A plain (non-contentEditable) copy field: Subject's input, Body's textarea. */
type PlainFieldElement = HTMLInputElement | HTMLTextAreaElement;

/**
 * Handler set for a plain field. Typed against the base `SyntheticEvent` so one
 * function satisfies all four slots (focus / keyboard / mouse / select) — each
 * only ever reads `currentTarget`.
 */
export interface PlainCaretProps {
  onFocus: (event: SyntheticEvent<PlainFieldElement>) => void;
  onKeyUp: (event: SyntheticEvent<PlainFieldElement>) => void;
  onMouseUp: (event: SyntheticEvent<PlainFieldElement>) => void;
  onSelect: (event: SyntheticEvent<PlainFieldElement>) => void;
}

/**
 * Handler set for the rich body. No `onFocus`: focus alone carries no usable
 * selection yet (the browser has not placed the caret), and `onBlur` is the
 * important one — it fires as the rep clicks the Tokens button, which is the
 * last moment the live selection still points into the editor.
 */
export interface RichCaretProps {
  onKeyUp: () => void;
  onMouseUp: () => void;
  onBlur: () => void;
}

/** How `insert` reads and writes the target plain field's text. */
export interface PlainTextAccess {
  getText: () => string;
  setText: (next: string) => void;
}

/**
 * Generic over the field-key union (`F`) so a host that knows its own keys —
 * `useCaretTracker<TokenTargetField>()` in the two step editors — gets
 * `lastField: TokenTargetField | null` instead of a bare `string`, and
 * `resolveTokenTarget` does not have to re-widen it. The internal maps stay
 * keyed by `string`; `F` is a caller-facing narrowing only.
 */
export interface CaretTracker<F extends string = string> {
  plainProps: (field: F) => PlainCaretProps;
  richProps: (field: F, rootRef: RefObject<HTMLElement | null>) => RichCaretProps;
  insert: (field: F, token: string, access: PlainTextAccess) => void;
  /**
   * The field key the rep most recently focused, typed in, clicked in or left
   * — `null` until they touch one of this tracker's fields at all. Recorded on
   * the same events the caret is (plus the rich body's `onBlur`);
   * `token-insert.ts#resolveTokenTarget` turns it into the insert target.
   */
  lastField: F | null;
}

/**
 * Resolve the contentEditable element the rich body actually edits, from the
 * wrapper the editor renders around `RichTextBodyField`.
 *
 * Queried rather than ref'd because the contentEditable div belongs to
 * `RichHtmlField` (core-owned, `web/app-home/src/components/ui/`), which
 * exposes no ref and no imperative handle. Adding one was the rejected
 * alternative in Key Technical Decision 10: it widens the review surface to a
 * core file for the same result a one-line query gets here.
 *
 * The wrapper itself is accepted when it IS the editable element, so a caller
 * that hands us the editor node directly still works.
 */
function resolveRichRoot(wrapper: HTMLElement | null): HTMLElement | null {
  if (wrapper === null) return null;
  if (wrapper.matches('[contenteditable="true"]')) return wrapper;
  return wrapper.querySelector<HTMLElement>('[contenteditable="true"]');
}

export function useCaretTracker<F extends string = string>(): CaretTracker<F> {
  /**
   * The last caret seen in each plain field, plus the element to restore it on.
   * Keyed by field so two open step editors cannot overwrite each other.
   */
  const plainCarets = useRef(
    new Map<string, { caret: number | null; el: PlainFieldElement | null }>(),
  );
  /** The last selection seen inside each rich body, cloned so later edits cannot mutate it. */
  const richRanges = useRef(new Map<string, Range>());
  /**
   * The wrapper element for each rich field, recorded by {@link richProps}.
   *
   * This is what tells `insert` that a field is rich rather than plain — it
   * cannot ask the field, since `insert`'s caller passes only a key and the
   * plain text accessors. Registering on render (rather than on first focus) is
   * deliberate: a rich body the rep has never clicked into still needs to
   * insert at its end, and there would be no record of it otherwise.
   *
   * Writing a ref during render is safe in this one direction: the map is never
   * READ during render (only inside `insert`, an event handler), so it cannot
   * make render impure or produce a torn UI, and the write is idempotent.
   */
  const richWrappers = useRef(new Map<string, RefObject<HTMLElement | null>>());
  /** See {@link CaretTracker.lastField}, and this module's doc for why this one is state. */
  const [lastField, setLastField] = useState<F | null>(null);

  /**
   * Mark `field` as the one the rep is in. Idempotent on purpose: returning
   * `prev` unchanged is what makes React bail out instead of re-rendering the
   * whole step editor on every keystroke within one field.
   */
  const markActive = useCallback((field: F) => {
    setLastField((prev) => (prev === field ? prev : field));
  }, []);

  const plainProps = useCallback(
    (field: F): PlainCaretProps => {
      const record = (event: SyntheticEvent<PlainFieldElement>) => {
        const el = event.currentTarget;
        plainCarets.current.set(field, { caret: el.selectionStart, el });
        markActive(field);
      };
      // All four events record the same thing; which one fires depends on how
      // the rep moved the caret (tab in, arrow keys, click, drag-select).
      return { onFocus: record, onKeyUp: record, onMouseUp: record, onSelect: record };
    },
    [markActive],
  );

  const richProps = useCallback(
    (field: F, rootRef: RefObject<HTMLElement | null>): RichCaretProps => {
      richWrappers.current.set(field, rootRef);
      const record = () => {
        // BEFORE the selection checks below, and unconditionally: "the rep was
        // last in this field" is true the moment one of these events fires
        // here, whether or not a usable Range came with it. A keystroke with
        // no selection, or a blur whose selection has already moved on, still
        // means the Tokens button should target this body — `insert`'s own
        // "no recorded caret → end of the field" fallback covers the caret
        // half. Skipping the mark alongside the range is how a rep who clicks
        // into the body, types, then clicks Tokens could still have the token
        // land in the Subject.
        markActive(field);
        const root = resolveRichRoot(rootRef.current);
        if (root === null) return;
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        // Anchored outside this editor: the rep's selection has moved on (or
        // belongs to a sibling card), so this field's record must not be
        // overwritten with a range pointing somewhere else.
        if (selection.anchorNode === null || !root.contains(selection.anchorNode)) return;
        richRanges.current.set(field, selection.getRangeAt(0).cloneRange());
      };
      return { onKeyUp: record, onMouseUp: record, onBlur: record };
    },
    [markActive],
  );

  const insertRich = useCallback((field: F, token: string, root: HTMLElement): void => {
    const saved = richRanges.current.get(field) ?? null;
    // A saved range survives re-renders but not a re-seed: `RichHtmlField`
    // replaces its own `innerHTML` when `seedKey` changes (AI compose, discard,
    // switching rung), which detaches the nodes the range points at. Inserting
    // into a detached range would silently drop the token, so anything no
    // longer inside the live editor is discarded in favour of the end of the
    // field — the same "no recorded caret → insert at end" rule the plain path
    // gets from `insertTokenAt(text, null, token)`.
    const usable =
      saved !== null && root.contains(saved.commonAncestorContainer) ? saved : null;
    const range = usable ?? document.createRange();
    if (usable === null) {
      range.selectNodeContents(root);
      range.collapse(false);
    }

    const node = document.createTextNode(token);
    // `deleteContents` first so inserting over a selection REPLACES it, the
    // same way typing would (and the same way `RichHtmlField`'s own paste path
    // does) rather than leaving the old text wrapped around the token.
    range.deleteContents();
    range.insertNode(node);
    range.setStartAfter(node);
    range.setEndAfter(node);

    root.focus();
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    richRanges.current.set(field, range.cloneRange());

    // The DOM now has the token but React does not: `RichHtmlField` is an
    // uncontrolled DOM island that commits through its own `onInput →
    // onChange(innerHTML)`. A bubbling native `input` event reaches React's
    // delegated listener and runs exactly that path, so the draft store gets
    // the token the same way it gets a keystroke — with no change to
    // `RichHtmlField` itself (Key Technical Decision 10).
    root.dispatchEvent(new Event('input', { bubbles: true }));
  }, []);

  const insertPlain = useCallback(
    (field: F, token: string, { getText, setText }: PlainTextAccess): void => {
      const saved = plainCarets.current.get(field) ?? null;
      const next = insertTokenAt(getText(), saved?.caret ?? null, token);
      setText(next.text);

      const el = saved?.el ?? null;
      // Record the post-insert caret even with no element to restore it on, so
      // two inserts in a row stack up (`{{First Name}}{{Last Name}}`) instead
      // of the second one landing back at the pre-insert position.
      plainCarets.current.set(field, { caret: next.caret, el });
      if (el === null) return;

      // Next frame, not now: `setText` routes through the draft store, so the
      // field's value is still the pre-insert string at this point and
      // `setSelectionRange` would be clamped to its length. One frame later
      // React has committed the new value and the caret lands after the token.
      requestAnimationFrame(() => {
        el.focus();
        try {
          el.setSelectionRange(next.caret, next.caret);
        } catch {
          // Input types like `email`/`number` reject the selection APIs. The
          // token itself is already committed; only the caret restore is lost.
        }
      });
    },
    [],
  );

  const insert = useCallback(
    (field: F, token: string, access: PlainTextAccess): void => {
      const root = resolveRichRoot(richWrappers.current.get(field)?.current ?? null);
      if (root !== null) insertRich(field, token, root);
      else insertPlain(field, token, access);
    },
    [insertPlain, insertRich],
  );

  return useMemo(
    () => ({ plainProps, richProps, insert, lastField }),
    [plainProps, richProps, insert, lastField],
  );
}
